import { Link, useNavigate, useLocation } from "react-router-dom";
import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, MessageCircle, Home as HomeIcon, Compass, Tag, Plane } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const tabs = [
  { id: "home", to: "/", label: "Home", Icon: HomeIcon, match: (p, h) => p === "/" && h !== "#pricing" },
  { id: "destinations", to: "/destinations", label: "Destinations", Icon: Compass, match: (p) => p.startsWith("/destinations") },
  { id: "pricing", to: "/#pricing", label: "Pricing", Icon: Tag, match: (p, h) => h === "#pricing" },
  { id: "start", to: "/register", label: "Start", Icon: Plane, match: (p) => p === "/register" },
];

// Toggle the native-style mobile bottom navigation. Kept off until requested.
const SHOW_MOBILE_TABBAR = false;

function MobileTabBar() {
  const { pathname, hash } = useLocation();
  return (
    <nav
      className="fixed inset-x-0 bottom-0 z-50 border-t border-hborder bg-softcream lg:hidden"
      style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
      aria-label="Mobile navigation"
      data-testid="mobile-tabbar">
      <div className="mx-auto grid max-w-page grid-cols-4">
        {tabs.map((t) => {
          const active = t.match(pathname, hash);
          const isStart = t.id === "start";
          return (
            <Link
              key={t.id}
              to={t.to}
              data-testid={`tab-${t.id}`}
              aria-current={active ? "page" : undefined}
              className={`flex flex-col items-center justify-center gap-1 py-2.5 text-[11px] font-semibold transition-colors ${
                isStart ? "text-heading" : active ? "text-tblue" : "text-navy/60"
              }`}>
              {isStart ? (
                <span className="grid h-9 w-9 place-items-center rounded bg-brandyellow ">
                  <t.Icon size={18} className="text-heading" strokeWidth={2} />
                </span>
              ) : (
                <t.Icon size={22} strokeWidth={active ? 2.4 : 1.9} />
              )}
              <span>{t.label}</span>
            </Link>
          );
        })}
      </div>
    </nav>
  );
}

const links = [
  { to: "/destinations", id: "destinations", label: "Destinations" },
  { to: "/how-it-works", id: "how-it-works", label: "How it works" },
  { to: "/applicants", id: "applicants", label: "For applicants" },
  { to: "/agencies", id: "agencies", label: "For agencies" },
  { to: "/#pricing", id: "pricing", label: "Pricing" },
  { to: "/help", id: "help", label: "Help" },
];

export function Logo({ to = "/" }) {
  const O = (
    <span className="relative inline-block">
      <span aria-hidden className="absolute left-1/2 top-[54%] -translate-x-1/2 -translate-y-1/2 rounded-full bg-navy" style={{ width: "0.36em", height: "0.36em" }} />
      <span className="relative">o</span>
    </span>
  );
  return (
    <Link to={to} data-testid="brand-logo" aria-label="Holavisto home"
      className="inline-flex items-center font-sans text-[1.7rem] font-extrabold leading-none tracking-tight text-brandyellow">
      H{O}laVist{O}
    </Link>
  );
}

export default function Nav() {
  const [open, setOpen] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();
  const appHome = () => {
    if (!user) return navigate("/login");
    if (["agency_owner", "agency_manager", "agency_agent"].includes(user.role)) return navigate("/agency");
    if (user.role === "reviewer") return navigate("/reviewer");
    navigate("/app");
  };

  return (
    <>
    <header className="sticky top-0 z-50 border-b border-hborder bg-softcream ">
      <div className="mx-auto flex h-20 max-w-page items-center justify-between px-5 sm:px-8 lg:px-12">
        <Logo />
        <nav className="hidden items-center gap-7 lg:flex" aria-label="Primary">
          {links.map((l) => (
            <Link key={l.id} to={l.to} data-testid={`nav-${l.id}`}
              className="text-[15px] font-medium text-navy transition-colors hover:text-tblue">
              {l.label}
            </Link>
          ))}
        </nav>
        <div className="hidden items-center gap-3 lg:flex">
          {user ? (
            <button onClick={appHome} data-testid="nav-dashboard-btn"
              className="rounded bg-navy px-6 py-2.5 text-[15px] font-semibold text-white transition-colors hover:bg-deepnavy">
              Go to dashboard
            </button>
          ) : (
            <>
              <Link to="/consultation" data-testid="nav-specialist"
                className="hidden items-center gap-1.5 text-[15px] font-medium text-navy transition-colors hover:text-tblue xl:inline-flex">
                <MessageCircle size={16} /> Talk to a specialist
              </Link>
              <Link to="/login" data-testid="nav-signin"
                className="rounded border border-hborder px-6 py-2.5 text-[15px] font-medium text-navy transition-colors hover:border-navy">
                Sign in
              </Link>
              <Link to="/register" data-testid="nav-start"
                className="rounded bg-brandyellow px-6 py-2.5 text-[15px] font-semibold text-heading transition-transform ">
                Start my visa
              </Link>
            </>
          )}
        </div>
        <button onClick={() => setOpen(!open)} className="grid h-10 w-10 place-items-center rounded-lg text-navy transition-colors hover:bg-cream lg:hidden" aria-label="Toggle menu" data-testid="nav-mobile-toggle">
          {open ? <X className="text-navy" /> : <Menu className="text-navy" />}
        </button>
      </div>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden border-t border-hborder bg-softcream lg:hidden"
            data-testid="nav-mobile-menu">
            <nav className="flex flex-col gap-1 px-5 py-4" aria-label="Mobile">
              {links.map((l, i) => (
                <motion.div key={l.id}
                  initial={{ opacity: 0, x: -12 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.05 + i * 0.05 }}>
                  <Link to={l.to} onClick={() => setOpen(false)} className="block rounded-lg px-2 py-2.5 text-base text-navy transition-colors hover:bg-cream">{l.label}</Link>
                </motion.div>
              ))}
              <div className="mt-2 flex flex-col gap-2">
                {user ? (
                  <button onClick={appHome} className="rounded bg-navy px-5 py-2.5 text-sm font-semibold text-white">Go to dashboard</button>
                ) : (
                  <>
                    <Link to="/login" onClick={() => setOpen(false)} className="rounded border border-hborder px-5 py-2.5 text-center text-sm text-navy">Sign in</Link>
                    <Link to="/register" onClick={() => setOpen(false)} className="rounded bg-brandyellow px-5 py-2.5 text-center text-sm font-semibold text-heading">Start my visa</Link>
                  </>
                )}
              </div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
    {SHOW_MOBILE_TABBAR && <MobileTabBar />}
    </>
  );
}
